import { endpoints } from "../endpoints";
import type {
  CurrentUser,
  ApiKey,
  Settings,
  Interview,
  CreateInterviewPayload,
  PromptTemplate,
  CreatePromptTemplatePayload,
  UpdatePromptTemplatePayload,
  InterviewConfig,
  CreateInterviewConfigPayload,
  LoginCredentials,
  RegisterPayload,
  AuthTokens,
  AccountType,
  Organization,
  OrganizationMember,
  OrganizationInvitation,
  OrganizationStats,
  UpdateOrganizationPayload,
  BulkInvitePayload,
  AcceptInvitationPayload,
  Feedback,
  CreateFeedbackPayload,
  UpdateFeedbackPayload,
  InterviewGrowthStats,
  PaginatedCompletedInterviewSummary,
  PaginatedInterviewsByStatus,
  ForgotPasswordPayload,
  ResetPasswordPayload,
  UserCategories,
  DefaultTemplates,
  PromptTemplateCategory,
  InterviewResult,
  ApiCreateInterviewPayload,
  CandidateSummary,
  JobPositionAnalytics,
  PaginatedResponse,
  Resume,
  TopCandidate,
  ApiKeyWithSecret,
  ActivityLogEntry,
  InterviewStatusFilters,
  UserPermissions,
} from "../types";

// A helper function to make authenticated API requests
const apiClient = async (url: string, options: RequestInit = {}, signal?: AbortSignal) => {
  const token = localStorage.getItem("authToken");
  const headers = new Headers(options.headers || {});

  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  // Do not set Content-Type for FormData, the browser handles it with the boundary
  if (
    options.body &&
    !(options.body instanceof FormData) &&
    !(options.body instanceof URLSearchParams)
  ) {
    headers.set("Content-Type", "application/json");
  }

  const isGetRequest =
    !options.method || options.method.toUpperCase() === "GET";

  const finalOptions = {
    ...options,
    headers,
    // Keep 'no-store' for browser cache bypass
    ...(isGetRequest && { cache: "no-store" as RequestCache }),
    signal: signal || options.signal, // Pass the signal here
  };

  let finalUrl = url;
  if (isGetRequest && finalOptions.cache === "no-store") {
    // FIX: Append a cache-busting timestamp parameter for CDN/external cache bypass
    try {
      const urlObject = new URL(finalUrl);
      urlObject.searchParams.set("_ts", Date.now().toString());
      finalUrl = urlObject.toString();
    } catch (e) {
      // Handle case where URL might be relative or malformed, though unlikely with `fetch`
      finalUrl = `${finalUrl}${
        finalUrl.includes("?") ? "&" : "?"
      }__ts=${Date.now()}`;
    }
  }

  const response = await fetch(finalUrl, finalOptions);

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ detail: "An unknown error occurred." }));
    throw new Error(
      errorData.detail || errorData.message || "API request failed"
    );
  }

  if (
    response.status === 204 ||
    response.headers.get("content-length") === "0"
  ) {
    return null;
  }

  return response.json();
};

// A helper function for public API requests (without Authorization header)
const publicApiClient = async (url: string, options: RequestInit = {}, signal?: AbortSignal) => {
  const headers = new Headers(options.headers || {});

  if (
    options.body &&
    !(options.body instanceof FormData) &&
    !(options.body instanceof URLSearchParams)
  ) {
    headers.set("Content-Type", "application/json");
  }

  const isGetRequest =
    !options.method || options.method.toUpperCase() === "GET";

  const finalOptions = {
    ...options,
    headers,
    // Keep 'no-store' for browser cache bypass
    ...(isGetRequest && { cache: "no-store" as RequestCache }),
    signal: signal || options.signal, // Pass the signal here
  };

  let finalUrl = url;
  if (isGetRequest && finalOptions.cache === "no-store") {
    // FIX: Append a cache-busting timestamp parameter for CDN/external cache bypass
    try {
      const urlObject = new URL(finalUrl);
      urlObject.searchParams.set("_ts", Date.now().toString());
      finalUrl = urlObject.toString();
    } catch (e) {
      // Handle case where URL might be relative or malformed, though unlikely with `fetch`
      finalUrl = `${finalUrl}${
        finalUrl.includes("?") ? "&" : "?"
      }__ts=${Date.now()}`;
    }
  }

  const response = await fetch(finalUrl, finalOptions);

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ detail: "An unknown error occurred." }));
    throw new Error(
      errorData.detail || errorData.message || "API request failed"
    );
  }

  if (
    response.status === 204 ||
    response.headers.get("content-length") === "0"
  ) {
    return null;
  }

  return response.json();
};

// --- Authentication, User, & Settings ---
export const login = async (
  credentials: LoginCredentials
): Promise<AuthTokens> => {
  const formData = new URLSearchParams();
  formData.append("username", credentials.email);
  formData.append("password", credentials.password);

  return apiClient(endpoints.auth.login, {
    method: "POST",
    body: formData,
  });
};

export const getAccountTypes = async (): Promise<{
  account_types: AccountType[];
}> => {
  return apiClient(endpoints.auth.accountTypes);
};

export const register = async (
  payload: RegisterPayload
): Promise<CurrentUser> => {
  const backendPayload: any = {
    email: payload.email,
    username: payload.username,
    full_name: payload.full_name,
    password: payload.password,
    account_type: payload.account_type || "individual",
    selected_categories: payload.selected_categories,
  };
  if (payload.account_type === "company" && payload.organization) {
    backendPayload.organization = payload.organization;
  }
  return apiClient(endpoints.auth.register, {
    method: "POST",
    body: JSON.stringify(backendPayload),
  });
};

export const logout = async (): Promise<void> => {
  const token = localStorage.getItem("authToken");
  if (!token) return;
  try {
    await fetch(endpoints.auth.logout, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
  } catch (error) {
    console.error("Logout API error:", error);
  }
};

export const forgotPassword = (
  data: ForgotPasswordPayload
): Promise<{ message: string }> => {
  return publicApiClient(endpoints.auth.forgotPassword, {
    method: "POST",
    body: JSON.stringify(data),
  });
};

export const resetPassword = (
  data: ResetPasswordPayload
): Promise<{ message: string }> => {
  return publicApiClient(endpoints.auth.resetPassword, {
    method: "POST",
    body: JSON.stringify(data),
  });
};

export const verifyResetToken = (
  token: string
): Promise<{ valid: boolean }> => {
  return publicApiClient(endpoints.auth.verifyResetToken(token));
};

export const sendVerificationEmail = (
  email: string
): Promise<{ message: string }> => {
  return publicApiClient(endpoints.auth.resendVerification, {
    method: "POST",
    body: JSON.stringify({ email }),
  });
};

export const verifyEmail = (token: string): Promise<{ message: string }> => {
  return publicApiClient(endpoints.auth.verifyEmail(token), { method: "POST" });
};

export const getCurrentUser = (): Promise<CurrentUser> => {
  return apiClient(endpoints.users.me);
};

export const updateMe = (data: {
  full_name: string;
  is_active?: boolean;
}): Promise<CurrentUser> => {
  return apiClient(endpoints.users.updateMe, {
    method: "PUT",
    body: JSON.stringify(data),
  });
};

export const updateUserCategories = (
  data: UserCategories
): Promise<CurrentUser> => {
  return apiClient(endpoints.users.updateCategories, {
    method: "PUT",
    body: JSON.stringify(data),
  });
};

export const getUserDefaultTemplates = (): Promise<DefaultTemplates> => {
  return apiClient(endpoints.users.defaultTemplates);
};

export const getSettings = async (): Promise<Settings> => {
  return {
    allow_public_signup: false,
    enforce_mfa: true,
    data_retention_days: 90,
  };
};
export const updateSettings = async (
  newSettings: Partial<Settings>
): Promise<Settings> => {
  return {
    allow_public_signup: false,
    enforce_mfa: true,
    data_retention_days: 90,
    ...newSettings,
  };
};

// --- Organization ---
export const getMyOrganization = (): Promise<Organization> =>
  apiClient(endpoints.organizations.myOrganization);

export const updateMyOrganization = (
  data: UpdateOrganizationPayload
): Promise<Organization> =>
  apiClient(endpoints.organizations.updateMyOrganization, {
    method: "PUT",
    body: JSON.stringify(data),
  });

export const uploadCompanyLogo = (file: File): Promise<Organization> => {
  const formData = new FormData();
  formData.append("file", file);

  return apiClient(endpoints.organizations.uploadCompanyLogo, {
    method: "POST",
    body: formData,
  });
};

export const deleteCompanyLogo = (): Promise<{ message: string }> => {
  return apiClient(endpoints.organizations.deleteCompanyLogo, {
    method: "DELETE",
  });
};

export const getOrganizationStats = (): Promise<OrganizationStats> =>
  apiClient(endpoints.organizations.stats);

export const getOrganizationMembers = (): Promise<OrganizationMember[]> =>
  apiClient(endpoints.organizations.listMembers);

export const removeOrganizationMember = (memberId: string): Promise<void> =>
  apiClient(endpoints.organizations.removeMember(memberId), {
    method: "DELETE",
  });

export const leaveOrganization = (): Promise<{ message: string }> =>
  apiClient(endpoints.organizations.leave, { method: "POST" });

export const getInvitations = (
  status?: "pending" | "accepted" | "declined" | "expired"
): Promise<OrganizationInvitation[]> => {
  const url = new URL(endpoints.organizations.listInvitations);
  if (status) {
    url.searchParams.append("status", status);
  }
  return apiClient(url.toString());
};

export const sendInvitations = (
  data: BulkInvitePayload
): Promise<OrganizationInvitation[]> =>
  apiClient(endpoints.organizations.createInvitations, {
    method: "POST",
    body: JSON.stringify(data),
  });

export const acceptInvitation = (
  token: string,
  data: AcceptInvitationPayload
): Promise<void> => {
  return apiClient(endpoints.organizations.acceptInvitation(token), {
    method: "POST",
    body: JSON.stringify(data),
  });
};

export const declineInvitation = (token: string): Promise<void> => {
  return apiClient(endpoints.organizations.declineInvitation(token), {
    method: "POST",
  });
};

export const sendWelcomeEmail = (
  email: string
): Promise<{ message: string }> => {
  return apiClient(endpoints.organizations.sendWelcomeEmail, {
    method: "POST",
    body: JSON.stringify({ user_email: email }),
  });
};

export const banOrganizationMember = (
  memberId: string,
  reason?: string
): Promise<void> => {
  const payload = reason ? { reason } : {};
  return apiClient(endpoints.organizations.banMember(memberId), {
    method: "POST",
    body: JSON.stringify(payload),
  });
};

export const unbanOrganizationMember = (memberId: string): Promise<void> => {
  return apiClient(endpoints.organizations.unbanMember(memberId), {
    method: "POST",
  });
};

export const getBannedOrganizationMembers = (): Promise<
  OrganizationMember[]
> => {
  return apiClient(endpoints.organizations.listBannedMembers);
};

// --- Public Invitation Functions ---
export const getPublicInvitationDetails = (
  token: string
): Promise<
  OrganizationInvitation & { user_exists: boolean; organization_name: string }
> => {
  return publicApiClient(endpoints.organizations.getInvitationDetails(token));
};

export const publicAcceptInvitation = (
  token: string,
  data?: { password?: string; full_name?: string }
): Promise<void> => {
  return publicApiClient(endpoints.organizations.acceptInvitation(token), {
    method: "POST",
    body: JSON.stringify({ token, ...data }),
  });
};

export const publicDeclineInvitation = (token: string): Promise<void> => {
  return publicApiClient(endpoints.organizations.declineInvitation(token), {
    method: "POST",
  });
};

// --- API Keys ---
export const getApiKeys = (): Promise<ApiKey[]> =>
  apiClient(endpoints.apiKeys.base);

export const createApiKey = (data: {
  name: string;
}): Promise<ApiKeyWithSecret> =>
  apiClient(endpoints.apiKeys.base, {
    method: "POST",
    body: JSON.stringify(data),
  });

export const deactivateApiKey = (keyId: string): Promise<{ message: string }> =>
  apiClient(endpoints.apiKeys.deactivate(keyId), { method: "DELETE" });

// --- Interviews ---
export const getInterviews = (params?: {
  page?: number;
  size?: number;
  job_position?: string;
  status?: string;
  candidate_name?: string;
  search_term?: string; // ADDED search_term
}): Promise<PaginatedResponse<Interview>> => {
  const searchParams = new URLSearchParams();
  if (params?.page !== undefined)
    searchParams.append("page", params.page.toString());
  if (params?.size !== undefined)
    searchParams.append("size", params.size.toString());
  if (params?.job_position)
    searchParams.append("job_position", params.job_position);
  if (params?.status) searchParams.append("status", params.status);
  if (params?.candidate_name)
    searchParams.append("candidate_name", params.candidate_name);
  if (params?.search_term)
    // ADDED search_term
    searchParams.append("search_term", params.search_term);

  const url = `${endpoints.interviews.base}?${searchParams.toString()}`;
  return apiClient(url);
};

export const getLatestCandidates = (params: {
  page: number;
  size: number;
}): Promise<PaginatedResponse<Interview>> => {
  const searchParams = new URLSearchParams({
    page: String(params.page),
    size: String(params.size),
  });
  const url = `${
    endpoints.analytics.latestCandidates
  }?${searchParams.toString()}`;
  return apiClient(url);
};

export const createInterview = (
  data: CreateInterviewPayload
): Promise<Interview> => {
  return apiClient(endpoints.interviews.base, {
    method: "POST",
    body: JSON.stringify(data),
  });
};

export const getInterviewDetails = (
  interviewId: string
): Promise<Interview> => {
  return apiClient(endpoints.interviews.details(interviewId));
};

export const getInterviewDetailsByApiKey = (
  interviewId: string
): Promise<Interview & { interview_result: any; feedback: any }> => {
  return apiClient(endpoints.interviews.detailsByApiKey(interviewId));
};

export const getInterviewResult = (
  interviewId: string
): Promise<InterviewResult> => {
  return apiClient(endpoints.interviews.result(interviewId));
};

export const updateInterview = (
  interviewId: string,
  data: Partial<Interview>
): Promise<Interview> => {
  return apiClient(endpoints.interviews.update(interviewId), {
    method: "PUT",
    body: JSON.stringify(data),
  });
};

export const uploadResume = (
  interviewId: string,
  file: File
): Promise<Resume> => {
  const formData = new FormData();
  formData.append("file", file);

  return apiClient(endpoints.interviews.resume(interviewId), {
    method: "POST",
    body: formData,
  });
};

export const getResume = (interviewId: string): Promise<Resume> => {
  return apiClient(endpoints.interviews.resume(interviewId));
};

export const generateInterviewToken = (
  interviewId: string
): Promise<{ token: string; room_name: string; participant_name: string }> => {
  return apiClient(endpoints.interviews.token(interviewId), {
    method: "POST",
  });
};

export const generateInterviewJoinLink = (
  interviewId: string
): Promise<{
  url: string;
  token: string;
  room_name: string;
  participant_name: string;
}> => {
  return apiClient(endpoints.interviews.joinLink(interviewId), {
    method: "POST",
  });
};

export const apiCreateInterview = (
  data: ApiCreateInterviewPayload
): Promise<Interview> => {
  return apiClient(endpoints.interviews.apiCreate, {
    method: "POST",
    body: JSON.stringify(data),
  });
};

export const apiGenerateInterviewToken = (
  interviewId: string
): Promise<{ token: string; room_name: string; participant_name: string }> => {
  return apiClient(endpoints.interviews.apiToken(interviewId), {
    method: "POST",
  });
};

export const searchCandidates = (params: {
  job_position: string;
  page?: number;
  size?: number;
  sort_by?: string;
  sort_order?: "asc" | "desc";
}): Promise<PaginatedResponse<CandidateSummary>> => {
  const searchParams = new URLSearchParams({
    job_position: params.job_position,
  });
  if (params.page !== undefined)
    searchParams.append("page", String(params.page));
  if (params.size !== undefined)
    searchParams.append("size", String(params.size));
  if (params.sort_by) searchParams.append("sort_by", params.sort_by);
  if (params.sort_order) searchParams.append("sort_order", params.sort_order);

  const url = `${
    endpoints.interviews.searchCandidates
  }?${searchParams.toString()}`;
  return apiClient(url);
};

export const getJobPositionAnalytics = (
  params: { page?: number; size?: number } = {}
): Promise<PaginatedResponse<JobPositionAnalytics>> => {
  const searchParams = new URLSearchParams();
  if (params.page !== undefined)
    searchParams.append("page", String(params.page));
  if (params.size !== undefined)
    searchParams.append("size", String(params.size));

  const url = `${
    endpoints.interviews.jobPositionAnalytics
  }?${searchParams.toString()}`;
  return apiClient(url);
};

export const getInterviewPermissions = (): Promise<UserPermissions> => {
  return apiClient(endpoints.auth.permissions);
};

// --- Prompt Templates & Interview Configs ---
export const getPromptTemplates = (params?: {
  category?: string;
  include_public?: boolean;
}): Promise<PromptTemplate[]> => {
  const searchParams = new URLSearchParams();
  if (params?.category) searchParams.append("category", params.category);
  if (params?.include_public !== undefined)
    searchParams.append("include_public", params.include_public.toString());
  const url = `${endpoints.promptTemplates.base}?${searchParams.toString()}`;
  return apiClient(url);
};

export const getPromptTemplateCategories = (): Promise<
  PromptTemplateCategory[]
> => {
  return apiClient(endpoints.promptTemplates.categories);
};

export const createPromptTemplate = (
  data: CreatePromptTemplatePayload
): Promise<PromptTemplate> =>
  apiClient(endpoints.promptTemplates.base, {
    method: "POST",
    body: JSON.stringify(data),
  });

export const getPromptTemplate = (
  templateId: string
): Promise<PromptTemplate> =>
  apiClient(endpoints.promptTemplates.details(templateId));

export const updatePromptTemplate = (
  templateId: string,
  data: UpdatePromptTemplatePayload
): Promise<PromptTemplate> =>
  apiClient(endpoints.promptTemplates.update(templateId), {
    method: "PUT",
    body: JSON.stringify(data),
  });

export const deletePromptTemplate = (
  templateId: string
): Promise<{ message: string }> =>
  apiClient(endpoints.promptTemplates.delete(templateId), { method: "DELETE" });

export const createInterviewConfig = (
  data: CreateInterviewConfigPayload
): Promise<InterviewConfig> => {
  return apiClient(endpoints.promptTemplates.createInterviewConfig, {
    method: "POST",
    body: JSON.stringify(data),
  });
};

export const getInterviewConfig = (
  interviewId: string
): Promise<InterviewConfig> => {
  return apiClient(endpoints.promptTemplates.getInterviewConfig(interviewId));
};

// --- Feedback ---
export const submitFeedback = (
  data: CreateFeedbackPayload
): Promise<Feedback> =>
  publicApiClient(endpoints.feedback.submit, {
    method: "POST",
    body: JSON.stringify(data),
  });

export const submitMemberFeedback = (
  data: CreateFeedbackPayload
): Promise<Feedback> =>
  apiClient(endpoints.feedback.submitMember, {
    method: "POST",
    body: JSON.stringify(data),
  });

export const getInterviewFeedback = (
  interviewId: string,
  params: { page?: number; size?: number } = {}
): Promise<Feedback[] | PaginatedResponse<Feedback>> => {
  const url = new URL(endpoints.feedback.listForInterview(interviewId));
  if (params.page !== undefined)
    url.searchParams.append("page", String(params.page));
  if (params.size !== undefined)
    url.searchParams.append("size", String(params.size));
  return apiClient(url.toString());
};

export const getFeedbackDetails = (feedbackId: string): Promise<Feedback> => {
  return apiClient(endpoints.feedback.details(feedbackId));
};

export const updateFeedback = (
  feedbackId: string,
  data: UpdateFeedbackPayload
): Promise<Feedback> => {
  return apiClient(endpoints.feedback.update(feedbackId), {
    method: "PUT",
    body: JSON.stringify(data),
  });
};

export const deleteFeedback = (feedbackId: string): Promise<void> => {
  return apiClient(endpoints.feedback.delete(feedbackId), { method: "DELETE" });
};

// --- Analytics ---
interface GrowthStatsParams {
  period?: "weekly" | "monthly" | "yearly";
  start_date?: string; // YYYY-MM-DD
  end_date?: string; // YYYY-MM-DD
}

export const getInterviewGrowthStats = (
  params: GrowthStatsParams
): Promise<InterviewGrowthStats> => {
  const url = new URL(endpoints.analytics.interviewGrowth);
  if (params.period) {
    url.searchParams.append("period", params.period);
  }
  if (params.start_date && params.end_date) {
    url.searchParams.append("start_date", params.start_date);
    url.searchParams.append("end_date", params.end_date);
  }
  return apiClient(url.toString());
};

export const getInterviewsByStatus = (
  status: "pending" | "scheduled" | "in_progress" | "completed" | "cancelled",
  params: InterviewStatusFilters = {},
  signal?: AbortSignal
): Promise<PaginatedInterviewsByStatus> => {
  const url = new URL(endpoints.analytics.interviewsByStatus);
  url.searchParams.append("status", status);

  // Dynamically add params if they exist and are not null/undefined
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      url.searchParams.append(key, String(value));
    }
  });

  return apiClient(url.toString(), {}, signal);
};

export const getCompletedInterviewSummary = (
  page = 1,
  size = 10
): Promise<PaginatedCompletedInterviewSummary> => {
  const url = new URL(endpoints.analytics.completedSummary);
  url.searchParams.append("page", String(page));
  url.searchParams.append("size", String(size));
  return apiClient(url.toString());
};

/**
 * Retrieves the top-scoring candidates for each job position.
 * @returns A promise that resolves to an array of top candidates.
 */
export const getTopCandidates = (): Promise<TopCandidate[]> => {
  return apiClient(endpoints.analytics.topCandidates);
};

export const getDistinctJobPositions = (): Promise<string[]> => {
  return apiClient(endpoints.analytics.jobPositions);
};

export const getActivityLogs = (params: {
  skip?: number;
  limit?: number;
}): Promise<ActivityLogEntry[]> => {
  const searchParams = new URLSearchParams();
  if (params.skip !== undefined)
    searchParams.append("skip", String(params.skip));
  if (params.limit !== undefined)
    searchParams.append("limit", String(params.limit));

  const url = `${endpoints.admin.activityLogs}?${searchParams.toString()}`;
  return apiClient(url);
};